<template>
  <div>
    <div
      v-if="config.eventDescription"
      class="event-header event-info-container"
    >
      <h1 class="WEV_h39_Bold">
        {{ eventName }}
      </h1>
      <div class="description-container">
        <div class="event-information">
          <div v-if="!externalEvent" class="event-information__date">
            <IconCalendar color="base" />
            <div v-if="!onSameDay" class="MOBILE_h16_Medium">
              <div>
                {{ $t('eventInformation.time.start') }}: {{ startDate }},
                {{ startTime }}
              </div>
              <div>
                {{ $t('eventInformation.time.end') }}: {{ endDate }},
                {{ endTime }}
              </div>
              <div v-if="!isRecurringEvent">
                {{ $t('eventInformation.time.gates') }}: {{ gateTime }}
              </div>
            </div>
            <div v-else class="MOBILE_h16_Medium">
              <div>{{ startDate }}, {{ startTime }} - {{ endTime }}</div>
              <div v-if="!isRecurringEvent">
                {{ $t('eventInformation.time.gates') }}: {{ gateTime }}
              </div>
            </div>
          </div>
          <div v-if="showLocation" class="event-information__location">
            <IconLocation color="base" />
            <div class="MOBILE_h16_Medium">
              <a :href="encodedVenueUrl" target="_blank">
                <span>{{ venue.name }}</span>
                <span>{{ addressInfo }}</span>
              </a>
            </div>
          </div>
        </div>
        <div class="event-organizer">
          <IconUserTag dark :size="24" />
          <div>
            <div v-if="organizer">
              <span class="event-organizer__name MOBILE_h16_Medium">
                {{ organizer.displayName || organizer.name }}
              </span>
            </div>
            <div v-if="!externalEvent">
              <p v-if="showEmail" class="MOBILE_h16_Medium">
                <a
                  class="MOBILE_h16_Medium"
                  :href="`mailto:${event.contactEmail ? event.contactEmail : organizer.email}`"
                >
                  {{ $t('eventDescription.contactOrganizer') }}
                </a>
              </p>
              <a
                v-if="organizer && organizer.url"
                class="MOBILE_h16_Medium"
                :href="organizer.url"
                target="_blank"
                rel="noopener"
              >
                {{ $t('buyTickets.organizerInfo') }}
              </a>
            </div>
          </div>
        </div>
      </div>
      <v-tabs
        v-model="tab"
        class="mt-auto flex-grow-0"
        :height="tabHeight"
        background-color="transparent"
      >
        <v-tabs-slider></v-tabs-slider>
        <v-tab
          v-for="element in tabs"
          :key="element.name"
          @click="$emit('tabClicked', element.name)"
        >
          {{ element.label }}
        </v-tab>
      </v-tabs>
    </div>
    <v-window v-model="tab" class="event-container px-2 px-md-0 mx-md-2 mb-4">
      <v-window-item v-for="element in tabs" :key="element.name">
        <slot :name="element.name"></slot>
      </v-window-item>
    </v-window>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import { EVENT_STATES, EVENT_TYPES } from '../../utils/utils';
import IconCalendar from '@/components/general/icons/IconCalendar.vue';
import IconLocation from '@/components/general/icons/IconLocation.vue';
import IconUserTag from '@/components/general/icons/IconUserTag.vue';
import { GoogleMaps } from '~/utils/venue/location';
import { convertDateToDisplay } from '~/utils/date-time';

export default {
  name: 'EventInfoPreview',
  components: {
    IconCalendar,
    IconLocation,
    IconUserTag
  },
  props: {
    tabs: {
      type: Array,
      default() {
        return [];
      }
    },
    value: {
      type: Number,
      default() {
        return 0;
      }
    },
    event: {
      type: Object,
      default() {
        return {};
      }
    },
    organizer: {
      type: Object,
      default() {
        return {};
      }
    },
    venue: {
      type: Object,
      default() {
        return {};
      }
    },
    externalEvent: {
      type: Boolean,
      default: false
    },
    selectedOccurrence: {
      type: Object,
      default() {
        return null;
      }
    },
    selectedTimeslot: {
      type: Object,
      default() {
        return null;
      }
    },
    showOrganizerEmail: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      eventStates: EVENT_STATES,
      config: {
        eventDescription: true
      }
    };
  },
  computed: {
    isRecurringEvent() {
      return this.event.type === EVENT_TYPES.RECURRING;
    },
    showLocation() {
      return (
        this.event.type !== EVENT_TYPES.RECURRING ||
        this.selectedOccurrence != null
      );
    },
    eventName() {
      if (this.event.status === this.eventStates.CANCELED) {
        return `${this.event.name} [${this.$t(
          'myEvents.statusFilter.canceled'
        ).toUpperCase()}] `;
      }
      return this.event.name;
    },
    addressInfo() {
      return GoogleMaps.venueData(this.venue?.address);
    },
    encodedVenueUrl() {
      return GoogleMaps.getLinkToVenue(this.venue);
    },
    isXSDevice() {
      return this.$vuetify.breakpoint.width < 320;
    },
    isSmallDevice() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    tab: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    tabHeight() {
      return !this.isSmallDevice ? '1.5rem' : null;
    },
    eventStart() {
      let eventStartDate;
      if (this.selectedTimeslot) {
        eventStartDate = this.selectedTimeslot.startAt;
      } else if (this.selectedOccurrence) {
        eventStartDate = this.selectedOccurrence.startAt;
      } else {
        eventStartDate = this.event.startAt;
      }
      const startDateLocal = convertDateToDisplay(
        eventStartDate,
        this.event.timezoneName
      );
      return startDateLocal;
    },
    eventEnd() {
      let eventEndDate;
      if (this.selectedTimeslot) {
        eventEndDate = this.selectedTimeslot.endAt;
      } else if (this.selectedOccurrence) {
        eventEndDate = this.selectedOccurrence.endAt;
      } else {
        eventEndDate = this.event.endAt;
      }
      const endDateLocal = convertDateToDisplay(
        eventEndDate,
        this.event.timezoneName
      );
      return endDateLocal;
    },
    eventGateTime() {
      const gateTimeLocal = convertDateToDisplay(
        this.event.gateTime,
        this.event.timezoneName
      );
      return gateTimeLocal;
    },
    startDate() {
      return this.$d(this.eventStart, 'ddd_DD_MMMM_YYYY');
    },
    startTime() {
      return this.$d(this.eventStart, 'HH_mm');
    },
    endDate() {
      return this.$d(this.eventEnd, 'ddd_DD_MMMM_YYYY');
    },
    endTime() {
      return this.$d(this.eventEnd, 'HH_mm');
    },
    gateTime() {
      return this.$d(this.eventGateTime, 'HH_mm');
    },
    onSameDay() {
      return this.startDate === this.endDate;
    },
    hasPhoneNumber() {
      return this.event?.contactPhone || this.organizer?.phone;
    },
    showEmail() {
      const hasEmail = this.event.contactEmail || this.organizer.email;
      return this.showOrganizerEmail && hasEmail;
    },
    /**
     * Returns the well formatted name of the timezone from the database
     * automatically falls back to moment if necessary
     */
    timezoneName() {
      // find the name of the timezone based on the offset
      const momentTZName = moment.tz.names().find((timezoneName) => {
        return moment.tz(timezoneName).format('Z') === this.event.timezone;
      });

      return this.event.timezoneName || momentTZName;
    }
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const description = urlParams.get('description');
    if (description && description === 'false') {
      this.config.eventDescription = false;
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@/assets/_scss/_utils/variables.scss';
@import '@/assets/_scss/_utils/mixins.scss';

.event-info-container {
  padding: 1rem;
  margin: 0.75rem 6rem 2rem 6rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  @include respond-below(md) {
    gap: 0;
    margin: 0.75rem 0.5rem 2rem 0.5rem;
  }
}

svg {
  margin-right: 18px;
  // to align to the baseline
  margin-top: 3px;
  flex-shrink: 0;
  flex-grow: 0;
}

.description-container {
  display: flex;
  gap: 32px;

  @include respond-below(md) {
    flex-direction: column;
    gap: 0;
  }
}
.description-container > div {
  flex: 1;
}

.event-information {
  margin-top: 12px;

  span {
    color: var(--v-textPrimary-base);
  }

  &__date,
  &__location {
    display: flex;
    align-items: flex-start;
    margin-bottom: 25px;

    div {
      color: var(--v-textPrimary-base);
    }

    a {
      display: flex;
      flex-direction: column;
      color: var(--v-textPrimary-base);
    }
  }
}

.event-organizer {
  @include respond-above(md) {
    margin-top: 12px;
  }

  display: flex;
  align-items: flex-start;
  margin-bottom: 25px;

  span,
  p,
  a {
    color: var(--v-textPrimary-base);
  }

  &__name {
    text-transform: uppercase;
  }
}
</style>
